import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResult, Failure } from "./api-result";

/**
 * One read, with its three states kept apart — docs/24 §11.1.
 *
 * **A pending request never draws a finished sentence.** The shape this exists to
 * make impossible is
 * `loading ? <Empty>Уншиж байна…</Empty> : rows.length ? … : <Empty>Ноорог алга.</Empty>`
 * — one grey line, centred, in the same place, for «the answer has not arrived»
 * and for «the answer is zero».
 *
 * `Read<T>` has no state that means both, so a screen cannot write that shape by
 * accident: `loading` carries no data to render, and `ready` carries data even
 * when the data is an empty list.
 */
export type Read<T> =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly data: T }
  | { readonly status: "failed"; readonly failure: Failure };

export interface ReadHandle<T> {
  readonly read: Read<T>;
  readonly reload: () => void;
  /** The rows, or `null` while the answer has not arrived. What a register takes. */
  readonly rows: T | null;
}

export function useRead<T>(
  load: (signal: AbortSignal) => Promise<ApiResult<T>>,
  deps: readonly unknown[],
): ReadHandle<T> {
  const [read, setRead] = useState<Read<T>>({ status: "loading" });
  const [nonce, setNonce] = useState(0);
  // The loader is a fresh closure every render (a page writes it inline), and the
  // fetch must NOT re-run for that. It is held in a ref, written in an effect
  // rather than during render — the initialiser covers the first pass, and this
  // effect is declared before the fetch so an update lands first.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const controller = new AbortController();
    // Synchronising with an external system — a network read — is what an effect
    // is for, and resetting to `loading` is part of the subscription rather than
    // a derived render: §11.1 turns on a pending request being distinguishable
    // from a zero answer, and without this a re-read would keep drawing the
    // previous answer as if it were current.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRead({ status: "loading" });
    void (async () => {
      try {
        const result = await loadRef.current(controller.signal);
        if (controller.signal.aborted) return;
        setRead(result.ok ? { status: "ready", data: result.data } : { status: "failed", failure: result });
      } catch {
        // An abort is a screen that moved on, not a failure to report.
        if (!controller.signal.aborted) {
          setRead({
            status: "failed",
            failure: {
              ok: false,
              kind: "unreachable",
              status: 0,
              code: "admin.api.unreachable",
              detail: "",
              extra: {},
              fields: {},
            },
          });
        }
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  return { read, reload, rows: read.status === "ready" ? read.data : null };
}
